import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { IoSettingsOutline } from "react-icons/io5";
import { FaAngleRight, FaAngleLeft } from "react-icons/fa6";
import { Modal } from "bootstrap";
import {
  getVirtualCardDetails,
  blockVirtualCard,
  unblockVirtualCard,
  validateVirtualCardTermination,
  initiateVirtualCardTermination,
} from "../../../api/api";

import {
  MdOutlineAddCard,
  MdFormatListBulleted,
  MdOutlineAccountBalanceWallet,
  MdOutlineCreditCard,
  MdLockOutline,
  MdOutlineLocationOn,
  MdAcUnit,
  MdDeleteOutline,
  MdOutlineReceiptLong,
} from "react-icons/md";
import CardOperationSummary from "./CardOperationSummary";
import TransactionPinModal from "../../../components/TransactionPinModal";

const CardOptions = () => {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const [freezeCard, setFreezeCard] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cardDetails, setCardDetails] = useState(null);
  const [validationData, setValidationData] = useState(null);
  const [validatedTerminationPayload, setValidatedTerminationPayload] =
    useState(null);
  const [terminating, setTerminating] = useState(false);
  const [terminationReason, setTerminationReason] = useState("");
  const [isTerminationValidated, setIsTerminationValidated] = useState(false);
  const handlePinSubmit = () => {
    navigate(`/user/cards/card_option/${cardId}/withdraw`);
  };

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const response = await getVirtualCardDetails(cardId);
        const card = response.data?.data;
        if (card) {
          setCardDetails(card);
          setFreezeCard(card.status === "frozen" || card.status === "blocked");
        }
      } catch (error) {
        // console.error(error);
      }
    };
    fetchCard();
  }, [cardId]);

  const [freezeReason, setFreezeReason] = useState("");
  const [processingFreeze, setProcessingFreeze] = useState(false);

  const handleFreezeToggle = (e) => {
    // Open modal instead of directly freezing
    const modalEl = document.getElementById("freezeReasonModal");
    const modal = new Modal(modalEl);
    modal.show();
  };

  const handleConfirmFreeze = async () => {
    if (!freezeReason.trim()) {
      toast.error("Please provide a reason");
      return;
    }

    setProcessingFreeze(true);
    const newState = !freezeCard;
    const payload = { reason: freezeReason };

    try {
      if (newState) {
        await blockVirtualCard(cardId, payload);
        toast.success("Card frozen successfully");
      } else {
        await unblockVirtualCard(cardId, payload);
        toast.success("Card un-frozen successfully");
      }
      setFreezeCard(newState);

      const modalEl = document.getElementById("freezeReasonModal");
      const modal = Modal.getInstance(modalEl);
      if (modal) modal.hide();
      setFreezeReason("");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update card status",
      );
    } finally {
      setProcessingFreeze(false);
    }
  };

  const handleDeleteClick = () => {
    setValidationData(null);
    setValidatedTerminationPayload(null);
    setIsTerminationValidated(false);
    setTerminationReason("");
    const modal = new Modal(document.getElementById("deleteCardModal"));
    modal.show();
  };

  const handleDeleteCard = async () => {
    if (!terminationReason.trim()) {
      toast.error("Please provide a reason for termination");
      return;
    }
    
    setLoading(true);
    try {
      const payload = { reason: terminationReason };
      
      // Validate
      await validateVirtualCardTermination(cardId, payload);
      
      // Terminate
      await initiateVirtualCardTermination(cardId, payload);

      const modalEl = document.getElementById("deleteCardModal");
      const modal = Modal.getInstance(modalEl);
      if (modal) modal.hide();

      toast.success("Card deleted successfully!", {
        toastId: "delete-success-toast",
        className: "custom-toast",
      });
      navigate("/user/cards");
    } catch (error) {
      const errorMsg =
        error.response?.data?.errors?.reason?.[0] ||
        error.response?.data?.message ||
        "Failed to terminate card";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <section
      className="create__card__page card__options__page"
      style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        paddingBottom: "3rem",
      }}
    >
      <div className="container py-4 pt-5" style={{ maxWidth: "750px" }}>
        {/* Header */}
        <div className="d-flex align-items-center mb-5">
          <button
            onClick={() => navigate("/user/cards")}
            className="btn btn-light rounded-circle shadow-sm me-3 d-flex align-items-center justify-content-center"
            style={{
              width: "40px",
              height: "40px",
              border: "1px solid #E0E4EC",
            }}
          >
            <FaAngleLeft size={16} color="#19064B" />
          </button>
          <h4 className="mb-0 fw-bold" style={{ color: "#19064B" }}>
            Card Options
          </h4>
        </div>

        <div className="row g-4">
          {/* Manage Funds Section */}
          <div className="col-12">
            <h6
              className="text-muted fw-semibold mb-3 px-2 text-uppercase"
              style={{ fontSize: "13px", letterSpacing: "1px" }}
            >
              Manage Funds
            </h6>
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="list-group list-group-flush">
                {/* Fund Card */}
                <button
                  onClick={() =>
                    handleNavigation(`/user/cards/card_option/${cardId}`)
                  }
                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between p-4 border-bottom"
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="icon-wrapper d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#eef2ff",
                        color: "#4f46e5",
                      }}
                    >
                      <MdOutlineAddCard size={22} />
                    </div>
                    <div className="text-start">
                      <h6
                        className="mb-1 fw-semibold"
                        style={{ color: "#19064B" }}
                      >
                        Fund Card
                      </h6>
                      <p className="mb-0 text-muted small">
                        Add money to your virtual card balance
                      </p>
                    </div>
                  </div>
                  <FaAngleRight color="#a1a1aa" />
                </button>

                {/* Card Details */}
                <button
                  onClick={() =>
                    handleNavigation(
                      `/user/cards/card_option/${cardId}/details`,
                    )
                  }
                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between p-4 border-bottom"
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="icon-wrapper d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#eff6ff",
                        color: "#2563eb",
                      }}
                    >
                      <MdOutlineCreditCard size={23} />
                    </div>
                    <div className="text-start">
                      <h6
                        className="mb-1 fw-semibold"
                        style={{ color: "#19064B" }}
                      >
                        Card Details
                      </h6>
                      <p className="mb-0 text-muted small">
                        See card number, expiry date and billing information
                      </p>
                    </div>
                  </div>
                  <FaAngleRight color="#a1a1aa" />
                </button>

                {/* Card Transactions */}
                <button
                  onClick={() =>
                    handleNavigation(
                      `/user/cards/card_option/${cardId}/transactions`,
                    )
                  }
                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between p-4"
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="icon-wrapper d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#eff6ff",
                        color: "#3b82f6",
                      }}
                    >
                      <MdOutlineReceiptLong size={22} />
                    </div>
                    <div className="text-start">
                      <h6
                        className="mb-1 fw-semibold"
                        style={{ color: "#19064B" }}
                      >
                        Card Transactions
                      </h6>
                      <p className="mb-0 text-muted small">
                        View card transaction history
                      </p>
                    </div>
                  </div>
                  <FaAngleRight color="#a1a1aa" />
                </button>

                {/* Withdraw */}
                <button
                  data-bs-toggle="modal"
                  data-bs-target="#enterPinModal"
                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between p-4 border-bottom"
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="icon-wrapper d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#f0fdf4",
                        color: "#16a34a",
                      }}
                    >
                      <MdOutlineAccountBalanceWallet size={22} />
                    </div>
                    <div className="text-start">
                      <h6
                        className="mb-1 fw-semibold"
                        style={{ color: "#19064B" }}
                      >
                        Withdraw
                      </h6>
                      <p className="mb-0 text-muted small">
                        Move money from your card to your wallet
                      </p>
                    </div>
                  </div>
                  <FaAngleRight color="#a1a1aa" />
                </button>

                {/* Card Activity */}
                <button
                  onClick={() =>
                    navigate("/user/cards/activity-logs", { state: { cardId } })
                  }
                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between p-4 border-bottom"
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="icon-wrapper d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#fff7ed",
                        color: "#ea580c",
                      }}
                    >
                      <MdFormatListBulleted size={22} />
                    </div>
                    <div className="text-start">
                      <h6
                        className="mb-1 fw-semibold"
                        style={{ color: "#19064B" }}
                      >
                        Card Activity
                      </h6>
                      <p className="mb-0 text-muted small">
                        View all activities associated with your card
                      </p>
                    </div>
                  </div>
                  <FaAngleRight color="#a1a1aa" />
                </button>
              </div>
            </div>
          </div>

          {/* Security & Settings Section */}
          <div className="col-12 ">
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="list-group list-group-flush">
                {/* Freeze Card */}
                <button
                  onClick={handleFreezeToggle}
                  className="list-group-item list-group-item-action d-flex align-items-center justify-content-between p-4"
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="icon-wrapper d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "48px",
                        height: "48px",
                        backgroundColor: "#f5f3ff",
                        color: "#7c3aed",
                      }}
                    >
                      <MdAcUnit size={22} />
                    </div>
                    <div className="text-start">
                      <h6
                        className="mb-1 fw-semibold"
                        style={{ color: "#19064B" }}
                      >
                        Freeze Card
                      </h6>
                      <p className="mb-0 text-muted small">
                        Temporarily disable your card from working
                      </p>
                    </div>
                  </div>
                  <div
                    className="form-check form-switch fs-4 mb-0"
                    style={{ pointerEvents: "none" }}
                  >
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={freezeCard}
                      readOnly
                    />
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="col-12 mt-5">
            <h6
              className="text-danger fw-semibold mb-3 px-2 text-uppercase"
              style={{ fontSize: "13px", letterSpacing: "1px" }}
            >
              Danger Zone
            </h6>
            <div
              className="card border-danger border-opacity-25 shadow-sm rounded-4 overflow-hidden"
              style={{ backgroundColor: "#fff1f2" }}
            >
              <button
                onClick={handleDeleteClick}
                disabled={loading}
                className="btn w-100 d-flex align-items-center justify-content-between p-4 text-danger border-0 bg-transparent text-start"
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="icon-wrapper d-flex align-items-center justify-content-center rounded-circle bg-danger text-white"
                    style={{ width: "48px", height: "48px" }}
                  >
                    <MdDeleteOutline size={24} />
                  </div>
                  <div>
                    <h6 className="mb-1 fw-semibold">Terminate Card</h6>
                    <p className="mb-0 small text-danger text-opacity-75">
                      Permanently block and terminate this card
                    </p>
                  </div>
                </div>
                <span>{loading ? "..." : <FaAngleRight />}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* <!-- PIN Modal --> */}
      <TransactionPinModal
        id="enterPinModal"
        onSubmit={handlePinSubmit}
        title="Enter Transaction PIN"
        description="Enter your 4-digit PIN to securely withdraw from your card."
      />

      {/* <!-- Freeze Reason Modal --> */}
      <section
        className="modal fade"
        id="freezeReasonModal"
        tabIndex="-1"
        aria-labelledby="freezeReasonModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden pb-4">
            <div className="d-flex justify-content-end p-3 position-absolute top-0 end-0 z-1">
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                disabled={processingFreeze}
              ></button>
            </div>
            <div className="modal-body p-4 p-md-5">
              <div className="text-center mb-4 mt-2">
                <div
                  className="icon-wrapper mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: "64px",
                    height: "64px",
                    backgroundColor: "#f5f3ff",
                    color: "#7c3aed",
                  }}
                >
                  <MdAcUnit size={32} />
                </div>
                <h4 className="mb-2 fw-bold text-dark">
                  {freezeCard ? "Unfreeze Card" : "Freeze Card"}
                </h4>
                <p className="text-muted small px-2">
                  Please provide a reason for{" "}
                  {freezeCard ? "activating" : "freezing"} this card.
                </p>
              </div>
              <div className="form-group mb-4 text-start">
                <label className="form-label small fw-semibold text-muted mb-2">
                  Reason
                </label>
                <textarea
                  className="form-control bg-light border-0 p-3 rounded-3 shadow-sm"
                  rows="3"
                  placeholder={`Enter reason for ${
                    freezeCard ? "activating" : "freezing"
                  }...`}
                  value={freezeReason}
                  onChange={(e) => setFreezeReason(e.target.value)}
                  disabled={processingFreeze}
                  style={{ resize: "none" }}
                ></textarea>
              </div>
              <div className="d-flex align-items-center justify-content-center mt-4">
                <button
                  className="btn primary__btn w-100 py-3 rounded-3 fw-semibold shadow-sm"
                  onClick={handleConfirmFreeze}
                  disabled={processingFreeze || !freezeReason.trim()}
                >
                  {processingFreeze ? "Processing..." : "Submit Reason"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* <!-- Modal --> */}
      <div
        className="modal fade"
        id="deleteCardModal"
        tabIndex="-1"
        aria-labelledby="deleteCardModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
            <div className="d-flex justify-content-end p-3 position-absolute top-0 end-0 z-1">
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body p-4 p-md-5">
              <div className="text-center mb-4 mt-2">
                <div
                  className="icon-wrapper mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: "64px",
                    height: "64px",
                    backgroundColor: "#fef2f2",
                    color: "#ef4444",
                  }}
                >
                  <MdDeleteOutline size={32} />
                </div>
                <h4 className="fw-bold text-dark">Terminate Card</h4>
                <p className="text-muted small px-2">
                  Are you sure you want to terminate this card with number{" "}
                  <strong>
                    **** **** ****{" "}
                    {cardDetails?.last4
                      ? String(cardDetails.last4)
                          .replace(/\D/g, "")
                          .slice(-4)
                      : cardDetails?.last4 || "****"}
                  </strong>
                  ? This action is permanent and cannot be undone.
                </p> 
              </div>

              <div className="form-group mb-4 mt-3 text-start">
                <label className="form-label small fw-semibold text-muted mb-2">
                  Termination Reason
                </label>
                <textarea
                  className="form-control bg-light border-0 p-3 rounded-3 shadow-sm"
                  rows="3"
                  placeholder="Enter reason for terminating this card..."
                  value={terminationReason}
                  onChange={(e) => setTerminationReason(e.target.value)}
                  disabled={loading}
                  style={{ resize: "none" }}
                ></textarea>
              </div>

              <div className="d-flex align-items-center gap-3 justify-content-between mt-5">
                <button
                  className="btn primary__btn__outline w-50 py-3 rounded-3 fw-semibold"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger w-50 py-3 rounded-3 fw-semibold shadow-sm" style={{ fontSize: "15px" }}
                  onClick={handleDeleteCard}
                  disabled={loading || !terminationReason.trim()}
                >
                  {loading ? "Terminating..." : "Proceed"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CardOptions;
